import { useState, useMemo, useRef, type FormEvent, type ChangeEvent, type ReactNode } from 'react'
import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, Upload } from 'lucide-react'
import PhoneInput, { type PhoneInputHandle } from '@/components/PhoneInput'
import { sanitizeRichTextHtml } from '@/lib/sanitizeRichTextHtml'
import {
  type ChoiceStat,
  type CustomFieldDef,
  type EventTheme,
  type FieldCondition,
  type InitialFormValues,
  type RegistrationField,
  type TitleOption,
  FIELD_MAP,
  emptyFormState,
  genderedTitleValue,
  normalizeConditionText,
  normalizeConditions,
  splitConditionValues,
  titleSelectValue,
  visibleTitleOptions,
} from '@/lib/registrationFormShared'

interface Translations {
  title: string
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  organization: string
  position: string
  badgeType: string
  gender: string
  country: string
  city: string
  age: string
  idNumber: string
  passportNumber: string
  bloodType: string
  image: string
  pleaseSelect: string
  otherTitle?: string
  pleaseSpecifyTitle?: string
  register: string
  saveChanges?: string
  registrationClosed: string
  requiredFields: string
  invalidPhoneNumber: string
  choiceStatsLabel?: string
  choiceStatsNoResponses?: string
  choiceStatsResponseOne?: string
  choiceStatsResponses?: string
  privacyPolicy: string
  privacyPolicyAgreement: string
  back?: string
}

interface Props {
  event: EventTheme
  registrationFields: RegistrationField[]
  customFields: CustomFieldDef[]
  userTypes: string[]
  bloodTypes: string[]
  genderOptions: string[]
  countries: string[]
  titleFieldType?: 'text' | 'select'
  titleOptions?: TitleOption[]
  positionFieldType?: 'text' | 'select'
  positionOptions?: string[]
  lockedUserType?: string | null
  registrationLinkSlug?: string | null
  errors: Record<string, string>
  currentLocale: string
  translations: Translations
  submitUrl: string
  submitMethod?: 'post' | 'patch'
  submitLabel?: string
  mode?: 'create' | 'edit'
  initialForm?: InitialFormValues | null
  backUrl?: string
  headerExtra?: ReactNode
  sessionId?: number | null
}

export default function RegistrationForm({
  event,
  registrationFields,
  customFields,
  userTypes,
  bloodTypes,
  genderOptions,
  countries,
  titleFieldType = 'text',
  titleOptions = [],
  positionFieldType = 'text',
  positionOptions = [],
  lockedUserType,
  registrationLinkSlug,
  errors,
  currentLocale,
  translations: t,
  submitUrl,
  submitMethod = 'post',
  submitLabel,
  mode = 'create',
  initialForm,
  backUrl,
  headerExtra,
  sessionId,
}: Props) {
  const [form, setForm] = useState(() => emptyFormState(initialForm))
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(initialForm?.imageUrl || null)
  const [submitting, setSubmitting] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(mode === 'edit')
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false)
  const [phoneError, setPhoneError] = useState('')
  const phoneRef = useRef<PhoneInputHandle>(null)
  const sanitizedPrivacyPolicyHtml = useMemo(
    () => sanitizeRichTextHtml(event.privacyPolicyHtml || ''),
    [event.privacyPolicyHtml],
  )

  const visibleTitles = useMemo(
    () => visibleTitleOptions(titleOptions, currentLocale),
    [titleOptions, currentLocale],
  )

  const set = (key: string, value: string) =>
    setForm((current) => {
      if (key === 'gender') {
        return { ...current, gender: value, title: genderedTitleValue(visibleTitles, current.title, value) }
      }
      if (key === 'title') {
        return { ...current, title: genderedTitleValue(visibleTitles, value, current.gender) }
      }
      return { ...current, [key]: value }
    })

  const customFieldAliases = (field: CustomFieldDef): string[] =>
    [field.name, field.nameEn, field.nameAr].filter((alias): alias is string => Boolean(alias))

  const getCustomValue = (field: CustomFieldDef): string => {
    for (const alias of customFieldAliases(field)) {
      const value = form.customFields[alias]
      if (value != null && value !== '') return value
    }
    return ''
  }

  const setCustom = (field: CustomFieldDef, value: string) =>
    setForm((currentForm) => {
      const nextCustomFields = { ...currentForm.customFields }
      customFieldAliases(field).forEach((alias) => {
        nextCustomFields[alias] = value
      })
      return { ...currentForm, customFields: nextCustomFields }
    })

  const handleImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const customByOrder = new Map<number, CustomFieldDef[]>()
  customFields.forEach((cf) => {
    const arr = customByOrder.get(cf.order) || []
    arr.push(cf)
    customByOrder.set(cf.order, arr)
  })

  const isRtl = currentLocale === 'ar'
  const inputBorder = 'ev-reg-input focus:ring-[var(--ev-primary)]'
  const inputStyle: React.CSSProperties = { borderColor: event.regInputBorderColor }
  const selectCls = `ev-reg-input flex h-8 w-full rounded-md border px-3 py-1 text-sm transition-colors focus:outline-none focus:ring-1${isRtl ? ' text-right' : ''}`

  const widthClass = (w: number, startNewRow = false) => {
    const span = w <= 33 ? 'sm:col-span-2' : w <= 50 ? 'sm:col-span-3' : 'sm:col-span-6'
    return startNewRow ? `${span} sm:col-start-1` : span
  }

  const radioOptionsClass = (w: number, showStats: boolean) => {
    const poll = showStats ? 'ev-reg-radio-poll ' : ''
    if (w <= 33) return `${poll}ev-reg-radio-options ev-reg-radio-options-grid grid grid-cols-1 sm:grid-cols-3 gap-2`
    if (w <= 50) return `${poll}ev-reg-radio-options ev-reg-radio-options-grid grid grid-cols-1 sm:grid-cols-2 gap-2`
    return `${poll}ev-reg-radio-options ev-reg-radio-options-grid grid grid-cols-1 gap-2`
  }

  const fieldReqMap = new Map<string, { required: boolean; width: number; startNewRow: boolean }>()
  registrationFields.forEach((rf) => {
    if (!rf.custom) fieldReqMap.set(rf.key, { required: rf.required, width: rf.width || 100, startNewRow: Boolean(rf.startNewRow) })
  })

  function getFieldValue(sourceField: string, sourceType: 'standard' | 'custom', sourceFieldId?: number | null): string {
    if (sourceType === 'standard') {
      const formKey = FIELD_MAP[sourceField]
      if (formKey) return (form as unknown as Record<string, string>)[formKey] || ''
    }
    if (sourceFieldId != null) {
      const customFieldById = customFields.find((field) => field.id === sourceFieldId)
      if (customFieldById) return getCustomValue(customFieldById)
    }
    const normalizedSourceField = normalizeConditionText(sourceField)
    const customFieldMatch = customFields.find((field) =>
      [field.name, field.nameEn, field.nameAr]
        .filter((alias): alias is string => Boolean(alias))
        .some((alias) => normalizeConditionText(alias) === normalizedSourceField),
    )
    if (customFieldMatch) return getCustomValue(customFieldMatch)
    return form.customFields[sourceField] || ''
  }

  function evaluateCondition(cond: FieldCondition): boolean {
    const val = getFieldValue(cond.sourceField, cond.sourceType, cond.sourceFieldId)
    const normalizedValue = normalizeConditionText(val)
    const normalizedConditionValue = normalizeConditionText(cond.value)
    switch (cond.operator) {
      case 'equals': return normalizedValue === normalizedConditionValue
      case 'not_equals': return normalizedValue !== normalizedConditionValue
      case 'contains': return normalizedValue.includes(normalizedConditionValue)
      case 'not_contains': return !normalizedValue.includes(normalizedConditionValue)
      case 'is_empty': return normalizedValue === ''
      case 'is_not_empty': return normalizedValue !== ''
      case 'is_one_of': return splitConditionValues(cond.value).includes(normalizedValue)
      default: return false
    }
  }

  function computeFieldState(defaultHidden: boolean, defaultRequired: boolean, conditionsInput: FieldCondition[] | string | null | undefined) {
    const conditions = normalizeConditions(conditionsInput)
    let visible = !defaultHidden
    let required = defaultRequired
    if (conditions.length === 0) return { visible, required }

    const showConds = conditions.filter((c) => c.action === 'show')
    const hideConds = conditions.filter((c) => c.action === 'hide')
    const reqConds = conditions.filter((c) => c.action === 'require')
    const unreqConds = conditions.filter((c) => c.action === 'unrequire')

    if (showConds.length > 0) {
      const anyShowMet = showConds.some(evaluateCondition)
      if (anyShowMet) visible = true
      else if (defaultHidden) visible = false
    }
    if (hideConds.length > 0 && hideConds.some(evaluateCondition)) visible = false
    if (reqConds.length > 0 && reqConds.some(evaluateCondition)) required = true
    if (unreqConds.length > 0 && unreqConds.some(evaluateCondition)) required = false
    return { visible, required }
  }

  const fieldStates = useMemo(() => {
    const states = new Map<string, { visible: boolean; required: boolean }>()
    registrationFields.forEach((rf) => {
      if (!rf.custom) states.set(`std:${rf.key}`, computeFieldState(rf.hidden, rf.required, rf.conditions))
    })
    customFields.forEach((cf) => {
      states.set(`custom:${cf.name}`, computeFieldState(cf.hidden, cf.required, cf.conditions))
    })
    return states
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, registrationFields, customFields])

  const submitForm = async (e: FormEvent) => {
    e.preventDefault()

    const formEl = e.currentTarget as HTMLFormElement
    if (!formEl.checkValidity()) {
      formEl.reportValidity()
      return
    }

    for (const cf of customFields) {
      const state = fieldStates.get(`custom:${cf.name}`)
      if (state && !state.visible) continue
      const isRequired = state?.required ?? cf.required
      if (!isRequired || cf.fieldType !== 'check_box') continue
      const val = getCustomValue(cf)
      if (val !== '1' && val !== 'true') {
        const cfKey = cf.nameEn || cf.name
        const input = document.getElementById(`cf-${cfKey}`) as HTMLInputElement | null
        input?.focus()
        input?.reportValidity?.()
        return
      }
    }

    let phoneNumber = form.phoneNumber
    const phoneField = registrationFields.find((rf) => rf.key === 'Phone number' && !rf.custom)
    if (phoneField) {
      const phoneState = fieldStates.get('std:Phone number')
      if (!phoneState || phoneState.visible) {
        const phoneRequired = phoneState?.required ?? phoneField.required
        const phoneCheck = await phoneRef.current?.validatePhone({ required: phoneRequired })
        if (!phoneCheck?.valid) {
          setPhoneError(t.invalidPhoneNumber)
          return
        }
        setPhoneError('')
        if (phoneCheck.number) phoneNumber = phoneCheck.number
      }
    }

    setSubmitting(true)

    const data = new FormData()
    data.append('event_user[event_id]', event.id.toString())
    data.append('event_user[title]', form.title)
    if (form.otherTitle) data.append('event_user[other_title]', form.otherTitle)
    data.append('event_user[first_name]', form.firstName)
    data.append('event_user[last_name]', form.lastName)
    data.append('event_user[phone_number]', phoneNumber)
    data.append('event_user[email]', form.email)
    data.append('event_user[organization]', form.organization)
    data.append('event_user[position]', form.position)
    data.append('event_user[user_type]', lockedUserType || form.userType)
    data.append('event_user[gender]', form.gender)
    data.append('event_user[country]', form.country)
    data.append('event_user[city]', form.city)
    data.append('event_user[age]', form.age)
    data.append('event_user[id_number]', form.idNumber)
    data.append('event_user[passport_number]', form.passportNumber)
    data.append('event_user[blood_type]', form.bloodType)

    Object.entries(form.customFields).forEach(([k, v]) => {
      data.append(`event_user[custom_fields][${k}]`, v)
    })

    if (imageFile) data.append('event_user[image]', imageFile)
    if (event.privacyPolicyHtml) data.append('event_user[agree_terms]', agreeTerms ? '1' : '0')
    if (sessionId) data.append('session', String(sessionId))
    if (submitMethod === 'patch') data.append('_method', 'patch')

    router.post(submitUrl, data, {
      forceFormData: true,
      onFinish: () => setSubmitting(false),
    })
  }

  const renderInput = (fieldKey: string, formKey: string, label: string, type = 'text', opts: { required?: boolean; pattern?: string; wrapClass?: string } = {}) => (
    <div className={`space-y-1.5 ${opts.wrapClass || ''}`} key={fieldKey}>
      <Label htmlFor={formKey} className="text-sm font-medium" style={{ color: event.regTextColor }}>
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Input
        id={formKey}
        type={type}
        value={(form as unknown as Record<string, string>)[formKey] || ''}
        onChange={(e) => set(formKey, e.target.value)}
        placeholder={label}
        required={opts.required}
        pattern={opts.pattern}
        dir={type === 'tel' ? 'ltr' : 'auto'}
        className={`${inputBorder}${isRtl ? ' placeholder:text-right' : ''}`}
        style={inputStyle}
      />
      {errors[formKey] && <p className="text-xs text-red-500">{errors[formKey]}</p>}
    </div>
  )

  const renderTitleField = (required?: boolean, wrapClass = '') => {
    if (titleFieldType === 'select' && titleOptions.length > 0) {
      return (
        <>
          <div className={`space-y-1.5 ${wrapClass}`} key="Title">
            <Label htmlFor="title" className="text-sm font-medium" style={{ color: event.regTextColor }}>
              {t.title}{required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <select
              id="title"
              value={titleSelectValue(visibleTitles, form.title)}
              onChange={(e) => set('title', e.target.value)}
              required={required}
              className={selectCls}
              style={{ borderColor: event.regInputBorderColor }}
            >
              <option value="">{t.pleaseSelect}</option>
              {visibleTitles.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
          </div>
          {form.title === 'other' && (
            <div className={`space-y-1.5 ${wrapClass}`} key="otherTitle">
              <Label htmlFor="otherTitle" className="text-sm font-medium" style={{ color: event.regTextColor }}>
                {t.otherTitle || 'Specify title'}{required && <span className="text-red-500 ml-0.5">*</span>}
              </Label>
              <Input
                id="otherTitle"
                value={form.otherTitle}
                onChange={(e) => set('otherTitle', e.target.value)}
                placeholder={t.pleaseSpecifyTitle || t.otherTitle || 'Please specify title'}
                required={required}
                className={`${inputBorder}${isRtl ? ' placeholder:text-right' : ''}`}
                style={inputStyle}
              />
              {errors.otherTitle && <p className="text-xs text-red-500">{errors.otherTitle}</p>}
            </div>
          )}
        </>
      )
    }

    return renderInput('Title', 'title', t.title, 'text', { required, wrapClass })
  }

  const renderPositionField = (required?: boolean, wrapClass = '') => {
    if (positionFieldType === 'select' && positionOptions.length > 0) {
      return (
        <div className={`space-y-1.5 ${wrapClass}`} key="Position">
          <Label htmlFor="position" className="text-sm font-medium" style={{ color: event.regTextColor }}>
            {t.position}{required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <select
            id="position"
            value={form.position}
            onChange={(e) => set('position', e.target.value)}
            required={required}
            className={selectCls}
            style={{ borderColor: event.regInputBorderColor }}
          >
            <option value="">{t.pleaseSelect}</option>
            {positionOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          {errors.position && <p className="text-xs text-red-500">{errors.position}</p>}
        </div>
      )
    }

    return renderInput('Position', 'position', t.position, 'text', { required, wrapClass })
  }

  const renderSelect = (formKey: string, label: string, options: string[], required = false, wrapClass = '') => (
    <div className={`space-y-1.5 ${wrapClass}`} key={formKey}>
      <Label htmlFor={formKey} className="text-sm font-medium" style={{ color: event.regTextColor }}>
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <select
        id={formKey}
        value={(form as unknown as Record<string, string>)[formKey] || ''}
        onChange={(e) => set(formKey, e.target.value)}
        required={required}
        className={selectCls}
        style={{ borderColor: event.regInputBorderColor }}
      >
        <option value="">{t.pleaseSelect}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )

  const initialChoiceValue = (cf: CustomFieldDef) => {
    if (mode !== 'edit') return ''
    for (const alias of customFieldAliases(cf)) {
      const value = initialForm?.customFields?.[alias]
      if (value != null && value !== '') return value
    }
    return ''
  }

  const liveChoiceRowsFor = (cf: CustomFieldDef, selected: string) => {
    const counts = new Map((cf.choiceStats || []).map((row: ChoiceStat) => [row.label, Number(row.count) || 0]))
    const rows = (cf.options.length > 0 ? cf.options : (cf.choiceStats || []).map((row) => row.label)).map((label) => ({
      label,
      count: counts.get(label) || 0,
    }))
    const counted = initialChoiceValue(cf)
    if (selected !== counted) {
      const bump = (label: string, delta: number) => {
        const row = rows.find((entry) => entry.label === label)
        if (row) row.count = Math.max(0, row.count + delta)
      }
      if (counted) bump(counted, -1)
      if (selected) bump(selected, 1)
    }
    const total = rows.reduce((sum, row) => sum + row.count, 0)
    const maxCount = rows.reduce((max, row) => Math.max(max, row.count), 0)
    return { rows, total, maxCount }
  }

  const choiceRowStyle = (selected = false): React.CSSProperties => ({
    color: event.regTextColor || '#FFFFFF',
    borderColor: selected
      ? 'color-mix(in srgb, currentColor 55%, transparent)'
      : event.regInputBorderColor,
    backgroundColor: 'color-mix(in srgb, currentColor 8%, transparent)',
  })

  const choiceFillStyle = (pct: number, emphasized: boolean): React.CSSProperties => ({
    insetInlineStart: 0,
    width: `${Math.max(pct, 0)}%`,
    backgroundColor: pct <= 0
      ? 'transparent'
      : emphasized
        ? 'color-mix(in srgb, currentColor 46%, transparent)'
        : 'color-mix(in srgb, currentColor 26%, transparent)',
  })

  const renderChoiceStats = (cf: CustomFieldDef, selected: string) => {
    if (!cf.showChoiceStats) return null
    const { rows, total, maxCount } = liveChoiceRowsFor(cf, selected)
    if (rows.length === 0) return null
    return (
      <div className="ev-reg-choice-stats mt-2 space-y-2" aria-live="polite" aria-label={`${cf.name} results`}>
        {rows.map((row) => {
          const pct = total > 0 ? Math.round((row.count / total) * 100) : 0
          const leading = row.count > 0 && row.count === maxCount
          const selectedRow = selected === row.label
          return (
            <div
              key={row.label}
              className="ev-reg-choice-stat relative min-h-10 overflow-hidden rounded-md border"
              style={choiceRowStyle(selectedRow)}
            >
              <span
                className="ev-reg-choice-stat-fill pointer-events-none absolute inset-y-0 rounded-[inherit] transition-[width] duration-500"
                style={choiceFillStyle(pct, selectedRow || leading)}
              />
              <div className="relative z-10 flex min-h-10 items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="min-w-0 truncate">{row.label}</span>
                <span className="ev-reg-choice-stat-pct shrink-0 tabular-nums text-xs opacity-80" dir="ltr">
                  {pct}%
                </span>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderCustomField = (cf: CustomFieldDef) => {
    const cfKey = cf.nameEn || cf.name
    const state = fieldStates.get(`custom:${cf.name}`)
    if (state && !state.visible) return null
    const val = getCustomValue(cf)
    const wc = widthClass(cf.width, Boolean(cf.startNewRow))
    const isRequired = state?.required ?? cf.required
    switch (cf.fieldType) {
      case 'drop_down':
        return (
          <div className={`space-y-1.5 ${wc}`} key={`cf-${cfKey}`}>
            <Label className="text-sm font-medium" style={{ color: event.regTextColor }}>
              {cf.name}{isRequired && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <select value={val} onChange={(e) => setCustom(cf, e.target.value)} required={isRequired} className={selectCls} style={{ borderColor: event.regInputBorderColor }}>
              <option value="">{t.pleaseSelect}</option>
              {cf.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {renderChoiceStats(cf, val)}
          </div>
        )
      case 'radio_button': {
        const showStats = Boolean(cf.showChoiceStats)
        const { rows, total, maxCount } = showStats ? liveChoiceRowsFor(cf, val) : { rows: [] as { label: string; count: number }[], total: 0, maxCount: 0 }
        const countByLabel = new Map(rows.map((row) => [row.label, row.count]))
        return (
          <div className={`space-y-2 sm:col-span-6${cf.startNewRow ? ' sm:col-start-1' : ''}`} key={`cf-${cfKey}`}>
            <Label className="text-sm font-medium" style={{ color: event.regTextColor }}>
              {cf.name}{isRequired && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <div
              className={radioOptionsClass(cf.width || 100, showStats)}
              role="radiogroup"
              aria-label={cf.name}
              aria-live={showStats ? 'polite' : undefined}
            >
              {cf.options.map((o, optionIdx) => {
                const optionId = `cf-${cfKey}-${o.replace(/\s+/g, '-').toLowerCase()}`
                const count = countByLabel.get(o) || 0
                const pct = total > 0 ? Math.round((count / total) * 100) : 0
                const selectedRow = val === o
                const leading = count > 0 && count === maxCount
                const iconUrl = cf.optionIconUrls?.[optionIdx]
                return (
                  <label
                    key={`${optionIdx}-${o}`}
                    htmlFor={optionId}
                    className={showStats
                      ? 'ev-reg-choice-stat relative flex min-h-10 min-w-0 w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md border px-3 py-2 text-sm'
                      : 'ev-reg-radio-option flex min-w-0 w-full items-center gap-2 text-sm'}
                    style={showStats ? choiceRowStyle(selectedRow) : { color: event.regTextColor }}
                  >
                    {showStats && (
                      <span
                        className="ev-reg-choice-stat-fill pointer-events-none absolute inset-y-0 rounded-[inherit] transition-[width] duration-500"
                        style={choiceFillStyle(pct, selectedRow || leading)}
                      />
                    )}
                    <input
                      type="radio"
                      id={optionId}
                      name={`custom-${cfKey}`}
                      value={o}
                      checked={selectedRow}
                      onChange={() => setCustom(cf, o)}
                      required={isRequired && !val}
                      className="relative z-10 h-4 w-4 shrink-0"
                      style={{ accentColor: event.primaryColor }}
                    />
                    <span className="relative z-10 min-w-0 flex-1 inline-flex items-center gap-2">
                      {iconUrl && (
                        <img
                          src={iconUrl}
                          alt=""
                          className="ev-reg-radio-option-icon h-8 w-8 shrink-0 object-contain"
                        />
                      )}
                      <span className={showStats ? 'truncate' : 'break-words'}>{o}</span>
                    </span>
                    {showStats && (
                      <span className="ev-reg-choice-stat-pct relative z-10 shrink-0 tabular-nums text-xs opacity-80" dir="ltr">
                        {pct}%
                      </span>
                    )}
                  </label>
                )
              })}
            </div>
          </div>
        )
      }
      case 'check_box':
        return (
          <div className={`flex items-center gap-2 ${wc}`} key={`cf-${cfKey}`}>
            <input
              type="checkbox"
              id={`cf-${cfKey}`}
              checked={val === '1' || val === 'true'}
              onChange={(e) => setCustom(cf, e.target.checked ? '1' : '0')}
              required={isRequired}
              className="h-4 w-4 rounded"
              style={{ accentColor: event.primaryColor, borderColor: event.regInputBorderColor }}
            />
            <Label htmlFor={`cf-${cfKey}`} className="text-sm" style={{ color: event.regTextColor }}>
              {cf.name}{isRequired && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
          </div>
        )
      case 'text_area':
        return (
          <div className={`space-y-1.5 ${wc}`} key={`cf-${cfKey}`}>
            <Label className="text-sm font-medium" style={{ color: event.regTextColor }}>
              {cf.name}{isRequired && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <textarea value={val} onChange={(e) => setCustom(cf, e.target.value)} required={isRequired} rows={3} className="ev-reg-input flex w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-1" style={{ borderColor: event.regInputBorderColor, '--tw-ring-color': event.primaryColor } as React.CSSProperties} />
          </div>
        )
      default:
        return (
          <div className={`space-y-1.5 ${wc}`} key={`cf-${cfKey}`}>
            <Label className="text-sm font-medium" style={{ color: event.regTextColor }}>
              {cf.name}{isRequired && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            <Input value={val} onChange={(e) => setCustom(cf, e.target.value)} required={isRequired} placeholder={cf.name} className={`${inputBorder}${isRtl ? ' placeholder:text-right' : ''}`} style={inputStyle} />
          </div>
        )
    }
  }

  const renderFields = () => {
    const elements: React.ReactNode[] = []
    registrationFields.forEach((rf) => {
      const fmKey = FIELD_MAP[rf.key]
      const fm = fieldReqMap.get(rf.key)
      const wc = widthClass(fm?.width ?? 100, Boolean(fm?.startNewRow))
      const state = fieldStates.get(`std:${rf.key}`)
      if (fmKey && !rf.custom) {
        if (state && !state.visible) return
        const req = state?.required ?? fm?.required
        switch (rf.key) {
          case 'Title': elements.push(renderTitleField(req, wc)); break
          case 'First name': elements.push(renderInput('First name', 'firstName', t.firstName, 'text', { required: req ?? true, wrapClass: wc })); break
          case 'Last name': elements.push(renderInput('Last name', 'lastName', t.lastName, 'text', { required: req ?? true, wrapClass: wc })); break
          case 'Phone number': elements.push(
            <div className={`space-y-1.5 ${wc}`} key="Phone number">
              <Label htmlFor="phoneNumber" className="text-sm font-medium" style={{ color: event.regTextColor }}>
                {t.phoneNumber}{req && <span className="text-red-500 ml-0.5">*</span>}
              </Label>
              <PhoneInput ref={phoneRef} value={form.phoneNumber} onChangeNumber={(num) => { set('phoneNumber', num); if (phoneError) setPhoneError('') }} locale={currentLocale} containerClassName="iti--custom-border ev-reg-phone-input" containerStyle={{ '--iti-border-color': event.regInputBorderColor } as React.CSSProperties} inputProps={{ id: 'phoneNumber', required: req, className: 'ev-reg-input', style: { borderColor: event.regInputBorderColor } }} />
              {(phoneError || errors.phoneNumber) && <p className="text-xs text-red-500">{phoneError || errors.phoneNumber}</p>}
            </div>,
          ); break
          case 'Email': elements.push(renderInput('Email', 'email', t.email, 'email', { required: req ?? true, wrapClass: wc })); break
          case 'Organization': elements.push(renderInput('Organization', 'organization', t.organization, 'text', { required: req, wrapClass: wc })); break
          case 'Position': elements.push(renderPositionField(req, wc)); break
          case 'User type': if (userTypes.length > 0 && !lockedUserType) elements.push(renderSelect('userType', t.badgeType, userTypes, req ?? true, wc)); break
          case 'Gender': elements.push(renderSelect('gender', t.gender, genderOptions, req, wc)); break
          case 'Country': elements.push(renderSelect('country', t.country, countries, req, wc)); break
          case 'City': elements.push(renderInput('City', 'city', t.city, 'text', { required: req, wrapClass: wc })); break
          case 'Age': elements.push(renderInput('Age', 'age', t.age, 'text', { required: req, pattern: '^[1-9][0-9]?$|^100$', wrapClass: wc })); break
          case 'Id number': elements.push(renderInput('Id number', 'idNumber', t.idNumber, 'text', { required: req, pattern: '^[0-9]*$', wrapClass: wc })); break
          case 'Passport number': elements.push(renderInput('Passport number', 'passportNumber', t.passportNumber, 'text', { required: req, wrapClass: wc })); break
          case 'Blood type': elements.push(renderSelect('bloodType', t.bloodType, bloodTypes, req, wc)); break
          case 'Image':
            elements.push(
              <div className={`space-y-1.5 ${wc}`} key="image">
                <Label htmlFor="image" className="text-sm font-medium" style={{ color: event.regTextColor }}>{t.image}</Label>
                <div className="flex items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:opacity-80 transition-colors" style={{ borderColor: event.regInputBorderColor }}>
                    <Upload className="h-4 w-4 text-[#91918E]" />
                    <span>{imageFile?.name || t.image}</span>
                    <input type="file" accept="image/*" onChange={handleImage} className="hidden" />
                  </label>
                  {imagePreview && <img src={imagePreview} alt="Preview" className="h-16 w-16 rounded-md object-cover border" style={{ borderColor: event.regInputBorderColor }} />}
                </div>
              </div>,
            )
            break
        }
      }
      if (rf.custom) {
        const customs = customByOrder.get(rf.order)
        if (customs) customs.forEach((cf) => elements.push(renderCustomField(cf)))
      }
    })
    return elements
  }

  const buttonLabel = submitLabel || (mode === 'edit' ? (t.saveChanges || t.register) : t.register)

  if (event.ended) {
    return (
      <div className="rounded-lg border p-10 text-center" style={{ borderColor: event.regInputBorderColor, backgroundColor: event.regCardBgColor }}>
        <p className="text-lg font-medium" style={{ color: event.regTextColor }}>{t.registrationClosed}</p>
      </div>
    )
  }

  return (
    <>
      {headerExtra && <div className="ev-reg-wizard-extra">{headerExtra}</div>}
      {backUrl && (
        <div className="ev-reg-back-link mb-4">
          <Link href={backUrl} className="ev-reg-back-link-anchor text-sm underline">
            ← {t.back || 'Back'}
          </Link>
        </div>
      )}
      <form onSubmit={submitForm} className="ev-reg-form ev-reg-card ev-reg-form-card rounded-lg border p-6">
        <p className="ev-reg-required-note mb-4 text-xs opacity-50">{t.requiredFields}</p>
        <div className="ev-reg-fields grid grid-cols-1 gap-4 sm:grid-cols-6">{renderFields()}</div>

        {event.privacyPolicyHtml && (
          <div className="ev-reg-privacy mt-6 rounded-lg border p-4" style={{ borderColor: event.regInputBorderColor, backgroundColor: 'rgba(255,255,255,0.35)' }}>
            <div className="ev-reg-privacy-header flex items-center justify-between gap-3">
              <div className="ev-reg-privacy-copy">
                <h2 className="ev-reg-privacy-title text-base font-semibold" style={{ color: event.regTextColor }}>{t.privacyPolicy}</h2>
                <p className="ev-reg-privacy-hint mt-1 text-xs" style={{ color: event.regTextColor, opacity: 0.65 }}>{t.privacyPolicyAgreement}</p>
              </div>
              <Button type="button" variant="outline" className="ev-reg-privacy-trigger shrink-0" onClick={() => setPrivacyPolicyOpen(true)} style={{ borderColor: event.regInputBorderColor, color: event.regTextColor }}>
                {t.privacyPolicy}
              </Button>
            </div>
            <label className="ev-reg-privacy-consent mt-4 flex items-start gap-3 text-sm" style={{ color: event.regTextColor }}>
              <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} required className="ev-reg-privacy-checkbox mt-0.5 h-4 w-4 rounded" style={{ accentColor: event.primaryColor }} />
              <span className="ev-reg-privacy-consent-label">{t.privacyPolicyAgreement}</span>
            </label>
          </div>
        )}

        {event.privacyPolicyHtml && (
          <Dialog open={privacyPolicyOpen} onOpenChange={setPrivacyPolicyOpen}>
            <DialogContent className="ev-reg-privacy-dialog sm:max-w-2xl max-h-[85vh] overflow-hidden p-0">
              <DialogHeader className="ev-reg-privacy-dialog-header border-b border-[#E8E8E5] px-6 py-4">
                <DialogTitle className="ev-reg-privacy-dialog-title text-[#37352F]">{t.privacyPolicy}</DialogTitle>
              </DialogHeader>
              <div className="ev-reg-privacy-dialog-content overflow-y-auto px-6 py-5 text-sm leading-6 text-[#37352F]" style={{ maxHeight: 'calc(85vh - 72px)' }} dangerouslySetInnerHTML={{ __html: sanitizedPrivacyPolicyHtml }} />
            </DialogContent>
          </Dialog>
        )}

        {Object.keys(errors).length > 0 && (
          <div className="mt-4 rounded-md bg-red-50 p-3">
            <ul className="list-inside list-disc text-sm text-red-600">
              {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
            </ul>
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <Button type="submit" disabled={submitting} className="ev-reg-button h-11 min-w-[200px] text-base font-semibold" style={{ backgroundColor: event.regButtonColor || event.primaryColor, color: event.regButtonTextColor || '#FFFFFF' }}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {buttonLabel}
          </Button>
        </div>
      </form>
    </>
  )
}
